import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheckIcon, PlusIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { inputClass } from '../../components/ui/Field';
import { eventTitle, eventTypeClass, eventTypeLabel, eventTypeShort, formatDate, formatRange, functionLabel, glasbeniTipi, organizacijskiTipi, voiceLabel } from '../../utils/labels';
import type { EventType } from '../../types';
export function AdminEvents() {
  const {
    events,
    upcoming,
    expectedFor,
    attendance,
    today
  } = useApp();
  const [tip, setTip] = useState<EventType | 'vsi' | 'glasbeni' | 'organizacijski'>('vsi');
  const [status, setStatus] = useState<'prihajajoci' | 'pretekli' | 'vsi'>('prihajajoci');
  const seznam = useMemo(() => {
    return events.filter(e => tip === 'vsi' ? true : tip === 'glasbeni' ? glasbeniTipi.includes(e.type) : tip === 'organizacijski' ? organizacijskiTipi.includes(e.type) : e.type === tip).filter(e => status === 'vsi' ? true : status === 'prihajajoci' ? e.datum >= today : e.datum < today).sort((a, b) => status === 'pretekli' ? b.datum.localeCompare(a.datum) : a.datum.localeCompare(b.datum));
  }, [events, tip, status, today]);
  return <div className="space-y-5" data-id="element-1002" data-mp="u2ibpcag67c1">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-1003" data-mp="u3jobl7431vv">
        <div data-id="element-1004" data-mp="18215t16fbi5c">
          <h1 className="text-2xl font-semibold text-ink" data-id="element-1005" data-mp="182jckrqpperi">Dogodki</h1>
          <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1006" data-mp="1313r4ykmh68l">
            {upcoming.length} prihajajočih dogodkov v sezoni 2026/27
          </p>
        </div>
        <Link to="/admin/dogodki/nov" data-id="element-1007" data-mp="2ji81honj0nuy">
          <Button data-id="element-1008" data-mp="2ei3fjj3un0xc">
            <PlusIcon className="h-4 w-4" data-id="element-1009" data-mp="2eginc050bu2n" />
            Nov dogodek
          </Button>
        </Link>
      </div>

      <Card className="p-4" data-id="element-1010" data-mp="1c3e4zcp4wh1p">
        <div className="grid gap-3 sm:grid-cols-2 lg:w-1/2" data-id="element-1011" data-mp="1h2z0te90c9a7">
          <select value={tip} onChange={e => setTip(e.target.value as EventType | 'vsi' | 'glasbeni' | 'organizacijski')} aria-label="Tip dogodka" className={inputClass} data-id="element-1012" data-mp="1h1fili3sjo5s">
            <option value="vsi" data-id="element-1013" data-mp="tdxy87muvscu">Vsi tipi dogodkov</option>
            <option value="glasbeni" data-id="element-1014" data-mp="tbbhpihzm2ih">— Glasbeni dogodki —</option>
            {glasbeniTipi.map(t => <option key={t} value={t} data-id="element-1015" data-mp="xcna1vs8x1ud">
                {eventTypeLabel[t]}
              </option>)}
            <option value="organizacijski" data-id="element-1016" data-mp="174t0v26rhd7z">— Organizacijski dogodki —</option>
            {organizacijskiTipi.map(t => <option key={t} value={t} data-id="element-1017" data-mp="9q72au7z60l9">
                {eventTypeLabel[t]}
              </option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value as 'prihajajoci' | 'pretekli' | 'vsi')} aria-label="Status" className={inputClass} data-id="element-1018" data-mp="emxhlfbygs5m">
            <option value="prihajajoci" data-id="element-1019" data-mp="23telasahprt">Prihajajoči</option>
            <option value="pretekli" data-id="element-1020" data-mp="3tb60u4814tel">Pretekli</option>
            <option value="vsi" data-id="element-1021" data-mp="3pam2ixbpzjqv">Vsi</option>
          </select>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-1022" data-mp="3pcyhd1m6ldzp">
        <div className="overflow-x-auto viva-scroll" data-id="element-1023" data-mp="3kbsmoy8yngnj">
          <table className="w-full min-w-[880px] text-left text-sm" data-id="element-1024" data-mp="3kdls6a7u7uty">
            <thead className="border-b border-line bg-canvas text-[12px] uppercase tracking-wide text-ink-muted" data-id="element-1025" data-mp="3fj7w05lcpjo8">
              <tr data-id="element-1026" data-mp="3alzb3x8tj1ja">
                <th className="px-5 py-3 font-medium" data-id="element-1027" data-mp="3ajd35wi0mk0j">Datum</th>
                <th className="px-4 py-3 font-medium" data-id="element-1028" data-mp="35o7kd6jwtlgo">Dogodek</th>
                <th className="px-4 py-3 font-medium" data-id="element-1029" data-mp="31ljdh4eraomc">Tip</th>
                <th className="px-4 py-3 font-medium" data-id="element-1030" data-mp="31muewxkqbkaa">Za koga</th>
                <th className="px-4 py-3 font-medium" data-id="element-1031" data-mp="2wn9hirl7jjzo">Prisotnost</th>
                <th className="px-4 py-3" data-id="element-1032" data-mp="10ia5jyqqndm3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1033" data-mp="14k5enpewuvaf">
              {seznam.map(e => {
              const vpisano = !!attendance[e.id];
              return <tr key={e.id} className="transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1034" data-mp="qqw8ryq68rwn">
                    <td className="whitespace-nowrap px-5 py-3" data-id="element-1035" data-mp="vpz0khh551mo">
                      <span className="block font-medium text-ink" data-id="element-1036" data-mp="gu1btk9jqzkh">
                        {e.datumDo ? formatRange(e.datum, e.datumDo) : formatDate(e.datum)}
                      </span>
                      <span className="block text-[12px] text-ink-muted" data-id="element-1037" data-mp="7v65m86i5ph6">
                        {e.zacetek}
                        {e.konec ? `–${e.konec}` : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-1038" data-mp="228oxp2hrkfv3">
                      <Link to={`/admin/dogodki/${e.id}`} className="font-medium text-ink hover:text-green-700" data-id="element-1039" data-mp="276yt9e7ehih8">
                        {eventTitle(e)}
                      </Link>
                      <span className="block text-[12px] text-ink-muted" data-id="element-1040" data-mp="2h8t0s5q60g1r">{e.lokacija}</span>
                    </td>
                    <td className="px-4 py-3" data-id="element-1041" data-mp="2q38s00nvsmds">
                      <Badge className={eventTypeClass[e.type]} data-id="element-1042" data-mp="2q4adkz2iwr9r">{eventTypeShort[e.type]}</Badge>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-ink-muted" data-id="element-1043" data-mp="2lao0x6wd0ndy">
                      {e.obcinstvo === 'glasovi' && e.glasovi ? e.glasovi.map(v => voiceLabel[v]).join(', ') : e.obcinstvo === 'funkcije' && e.funkcije ? e.funkcije.map(f => functionLabel[f]).join(', ') : e.obcinstvo === 'izbrani' ? 'Izbrani člani' : e.obcinstvo === 'aktivni' ? 'Aktivni člani' : e.obcinstvo === 'sestavljeno' ? 'Sestavljen izbor' : 'Vsi in gostje'}
                      <span className="block text-[12px]" data-id="element-1044" data-mp="3eqle4hqdc5qy">{expectedFor(e).length} pričakovanih</span>
                    </td>
                    <td className="px-4 py-3" data-id="element-1045" data-mp="3jpeq25q0bop0">
                      {!e.steje ? <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-1046" data-mp="3opajjb12vxqb">Ne šteje</Badge> : vpisano ? <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1047" data-mp="3spkxpaz0xug9">
                          Vpisana
                        </Badge> : <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-id="element-1048" data-mp="6ba0hid7g6qs">Ni vpisana</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1049" data-mp="g97mdz7ldrg8">
                      <Link to={e.steje ? `/admin/prisotnost/${e.id}` : `/admin/dogodki/${e.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1050" data-mp="gasivudgvkgq">
                        <ClipboardCheckIcon className="h-3.5 w-3.5" data-id="element-1051" data-mp="1vrdmbu0so8o0" />
                        {e.steje ? 'Prisotnost' : 'Odpri'}
                      </Link>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}